import { ModuleConfig, Polyline, Postprocessing } from '@editor/services/PolylineExtraction/interfaces'

import { dist, distSq, dot, sub } from '../../utils/math'

export interface DecimateConfig extends ModuleConfig {
  /**
   * Douglas-Peucker epsilon tolerance
   */
  epsilon?: number
}

/**
 * Reduces point density in polylines using various decimation strategies
 */
export class Decimate extends Postprocessing {
  protected config: Required<DecimateConfig>

  constructor(config?: DecimateConfig) {
    super()
    this.config = {
      epsilon: 0.0015,
      verbose: config?.verbose ?? false,
      ...config,
    }
    this.validateConfig()
  }

  getName(): string {
    const { epsilon } = this.config
    return `Decimate(eps=${epsilon})`
  }

  run(polylines: Polyline[]): Polyline[] {
    this.logger?.setEnabled(this.config.verbose)

    const indexOfMax = (arr: Polyline) => {
      const start = arr[0]
      const end = arr[arr.length - 1]
      const seg = sub(end, start)
      const segLenSq = distSq(start, end)
      let maxDist = -1
      let idx = -1

      Array.from({ length: arr.length - 2 }, (_, i) => i + 1).forEach((i) => {
        const p = arr[i]
        const t = segLenSq === 0 ? 0 : dot(sub(p, start), seg) / segLenSq
        const proj = {
          x: start.x + t * seg.x,
          y: start.y + t * seg.y,
          z: start.z + t * seg.z,
        }

        const d = dist(p, proj)
        if (d > maxDist) {
          maxDist = d
          idx = i
        }
      })
      // Log the max distance and index for this segment
      this.logger?.log(`    ✂️ Decimate: indexOfMax ${idx}, maxDist ${maxDist}, arrLength ${arr.length}`)
      return { idx, maxDist }
    }

    const rdp = (arr: Polyline): Polyline => {
      if (arr.length < 3) {
        this.logger?.log(`    ✂️ Decimate:[RDP] Segment too short, returning as is: ${arr.length}`)
        return arr.slice()
      }
      const { idx, maxDist } = indexOfMax(arr)
      if (maxDist > this.config.epsilon && idx !== -1) {
        this.logger?.log(`    ✂️ Decimate:[RDP] Decimating: maxDist ${maxDist} > ${this.config.epsilon} at idx ${idx}`)
        const left = rdp(arr.slice(0, idx + 1))
        const right = rdp(arr.slice(idx))
        return [...left.slice(0, -1), ...right]
      }
      this.logger?.log(
        `    ✂️ Decimate:[RDP] No decimation needed for segment, returning endpoints only: ${arr.length}`,
      )
      return [arr[0], arr[arr.length - 1]]
    }

    // return polylines.map((pl) => rdp(pl))
    // Log before/after for each polyline
    const result = polylines.map((pl, i) => {
      const decimated = rdp(pl)
      if (decimated.length === pl.length) {
        this.logger?.log(`    Decimate: Polyline ${i}: No points removed (${pl.length} points)`)
      } else {
        this.logger?.log(`    Decimate: Polyline ${i}: Reduced from ${pl.length} to ${decimated.length} points`)
      }
      return decimated
    })

    this.logger?.setEnabled(true)
    return result
  }

  private validateConfig(): void {
    if (this.config.epsilon && this.config.epsilon < 0) {
      throw new Error('[Decimate] epsilon must be non-negative')
    }
  }
}
